import {
    createInertiaApp,
    http,
    type ResolvedComponent,
    router,
} from '@inertiajs/react';
import axios from 'axios';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import SessionContext from './components/SessionContext/SessionContext';
import StyleReset from './components/StyleReset/StyleReset';
import ThemeProvider from './components/ThemeProvider/ThemeProvider';
import { LocalizationProvider } from './customer-area/components/LocalizationProvider/LocalizationProvider';
import OverlayStatusProvider from './customer-area/components/OverlayStatusContext/OverlayStatusProvider';
import { ConfirmDialogProvider } from './customer-area/hooks/ui/useConfirmDialog';
import type PageProps from './customer-area/types/Inertia/PageProps';

import translationLoadersByLocale from 'virtual:translations/customerArea';

const pages = import.meta.glob<ResolvedComponent>(
    './customer-area/pages/{*.tsx,*/*.tsx}',
    { import: 'default' }
);

const CSRF_HEADER = 'X-CSRF-TOKEN' as const;

/*
 * Safari blocks the XSRF-TOKEN cookie inside a third-party iframe (#4412), thus
 * the token travels as a request header. The axios defaults hold the current
 * token: axios sends it on the JSON calls, and Inertia's own HTTP client reads
 * it from there for the page visits.
 */
const rememberCsrfToken = (token: string): void => {
    axios.defaults.headers.common[CSRF_HEADER] = token;
};

http.onRequest((config) => ({
    ...config,
    headers: {
        ...config.headers,
        [CSRF_HEADER]: axios.defaults.headers.common[CSRF_HEADER],
    },
}));

createInertiaApp<PageProps>({
    progress: {
        delay: 250,
        color: '#12C19B',
        includeCSS: true,
        showSpinner: false,
    },
    resolve: (name) => {
        const page = pages[`./customer-area/pages/${name}.tsx`];
        if (!page) {
            throw new Error(`Cannot find page with name "${name}"`);
        }

        return page();
    },
    setup: ({ el, App, props }) => {
        const { localization, theme, tenant } = props.initialPage.props;

        rememberCsrfToken(props.initialPage.props.csrfToken);

        router.on('navigate', (event) => {
            rememberCsrfToken(
                (event.detail.page.props as unknown as PageProps).csrfToken
            );
        });

        const loadTranslations =
            translationLoadersByLocale[localization.bundleLocale];

        if (!loadTranslations) {
            throw new Error(
                `No translations bundled for "${localization.bundleLocale}".`
            );
        }

        loadTranslations().then(({ default: translations }) => {
            createRoot(el).render(
                <StrictMode>
                    <StyleReset>
                        <ThemeProvider theme={theme}>
                            <LocalizationProvider
                                {...localization}
                                translations={translations}
                            >
                                <ConfirmDialogProvider>
                                    <SessionContext
                                        initialValue={{
                                            tenantId: tenant.id,
                                        }}
                                    >
                                        <OverlayStatusProvider>
                                            <App {...props} />
                                        </OverlayStatusProvider>
                                    </SessionContext>
                                </ConfirmDialogProvider>
                            </LocalizationProvider>
                        </ThemeProvider>
                    </StyleReset>
                </StrictMode>
            );
        });
    },
});
